import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase.js';
import { logClientError } from './errorLog.js';
import { friendlyError } from './friendlyError.js';
import { fetchContentBankItems } from './contentBank.js';

// ─────────────────────────────────────────────────────────────────────────
// Ask AI — in-page bridge (client half)
//
// Generated topic pages render in `<iframe sandbox="allow-scripts">` with
// no allow-same-origin (see README "Sandboxing") — they have no Firebase
// session, no token, and no direct network path to our backend. This file
// is the trusted go-between:
//
//   generated page  --postMessage-->  parent view  --httpsCallable-->  askAIInPage
//   generated page  <--postMessage--  parent view  <--------------------------
//
// injectAskAIBridge(html) gives the generated page a plain `window.askAI(
// question)` promise-based helper (documented for the model in
// functions/systemPrompt.js). attachAskAIBridge(iframe, context) is what
// runs in the parent — it listens for the iframe's request messages, calls
// the real Cloud Function with the signed-in user's session, and posts the
// answer back down. The iframe itself never touches a token or gets a
// direct network path; it only ever talks to its own parent.
// ─────────────────────────────────────────────────────────────────────────

// Explicit timeout (SDK default is 70s) — this should respond the moment an
// answer arrives, however long that takes, not cut off at some "typical"
// duration. Set just ABOVE the Cloud Function's own timeoutSeconds (540s,
// see functions/index.js) purely so THAT server-side limit is what actually
// surfaces if something is truly stuck, rather than this SDK call giving up
// first on a request that was still going to succeed. Also see
// injectAskAIBridge below, whose in-page promise timeout is set above this
// one for the same reason (outermost layer = most tolerant).
const askAIInPageFn = httpsCallable(functions, 'askAIInPage', { timeout: 560000 });

export async function askAIInPage({ question, courseTitle, topicTitle }) {
  const { data } = await askAIInPageFn({ question, courseTitle, topicTitle });
  return data.answer;
}

// Structured sibling of the above, same callable. Returns a PARSED,
// server-validated object rather than a string — see
// functions/contentSchemas.js for why the parsing lives on the server: the
// page is a sandboxed iframe that can neither retry cheaply nor tell a
// reworded reply from a wrong one, and every retry it makes is another
// wallet charge.
export async function askAIContentInPage({ kind, params, courseTitle, topicTitle }) {
  const { data } = await askAIInPageFn({ kind, params, courseTitle, topicTitle });
  return data.content;
}

const CANVAS_ENHANCEMENTS_MARKER = '<!--LL_CANVAS_ENHANCEMENTS_V1-->';

const CANVAS_ENHANCEMENTS_SCRIPT = `${CANVAS_ENHANCEMENTS_MARKER}<script>
  (function() {
    if (window.__llCanvasEnhancementsInstalled) return;
    window.__llCanvasEnhancementsInstalled = true;

    function ensureStyle() {
      if (document.getElementById('ll-canvas-enhancements-style')) return;
      var style = document.createElement('style');
      style.id = 'll-canvas-enhancements-style';
      style.textContent = [
        '.ll-canvas-retrofit-stage,.llui-canvas-stage{position:relative;}',
        '.ll-canvas-retrofit-fullscreen{position:absolute;top:10px;right:10px;z-index:50;width:36px;height:36px;border:1px solid rgba(15,23,42,.18);border-radius:999px;background:rgba(255,255,255,.92);color:#111827;font:700 14px/1 system-ui,-apple-system,sans-serif;box-shadow:0 8px 20px rgba(15,23,42,.16);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;}',
        '.ll-canvas-retrofit-fullscreen:hover{background:#fff;}',
        '.ll-canvas-retrofit-stage:fullscreen,.llui-canvas-stage:fullscreen{width:100vw!important;height:100vh!important;max-width:none!important;max-height:none!important;aspect-ratio:auto!important;background:#fff!important;overflow:hidden!important;}',
        '.ll-canvas-retrofit-stage.ll-canvas-retrofit-fullscreen-fallback,.llui-canvas-stage.ll-canvas-retrofit-fullscreen-fallback{position:fixed!important;inset:0!important;z-index:2147483000!important;width:100vw!important;height:100vh!important;max-width:none!important;max-height:none!important;aspect-ratio:auto!important;background:#fff!important;overflow:hidden!important;}',
        '.ll-canvas-retrofit-stage:fullscreen>canvas,.ll-canvas-retrofit-stage.ll-canvas-retrofit-fullscreen-fallback>canvas,.llui-canvas-stage:fullscreen>canvas,.llui-canvas-stage.ll-canvas-retrofit-fullscreen-fallback>canvas{width:100%!important;height:100%!important;display:block!important;}',
        '.ll-canvas-retrofit-stage:fullscreen .ll-canvas-retrofit-fullscreen,.llui-canvas-stage:fullscreen .ll-canvas-retrofit-fullscreen,.ll-canvas-retrofit-fullscreen-fallback .ll-canvas-retrofit-fullscreen{position:absolute!important;top:12px!important;right:12px!important;}'
      ].join('\\n');
      (document.head || document.documentElement).appendChild(style);
    }

    function emitResize() {
      setTimeout(function() { window.dispatchEvent(new Event('resize')); }, 0);
      setTimeout(function() { window.dispatchEvent(new Event('resize')); }, 80);
    }

    function fallbackStage(stage, on) {
      stage.classList.toggle('ll-canvas-retrofit-fullscreen-fallback', !!on);
      document.body.style.overflow = on ? 'hidden' : '';
      emitResize();
    }

    function toggleFullscreen(stage) {
      if (document.fullscreenElement === stage) {
        document.exitFullscreen().catch(function() { fallbackStage(stage, false); });
        return;
      }
      if (stage.classList.contains('ll-canvas-retrofit-fullscreen-fallback')) {
        fallbackStage(stage, false);
        return;
      }
      if (stage.requestFullscreen) {
        stage.requestFullscreen().then(emitResize).catch(function() { fallbackStage(stage, true); });
      } else {
        fallbackStage(stage, true);
      }
    }

    function stageFor(canvas) {
      var existing = canvas.closest('.llui-canvas-stage,.ll-canvas-retrofit-stage');
      if (existing) return existing;
      var parent = canvas.parentElement;
      if (!parent) return null;
      var parentClass = parent.className || '';
      var canReuseParent = parent.children.length === 1
        || /(^|\\s)(ll-canvas-wrap|canvas-wrap|canvas-stage|stage|sim-stage)(\\s|$)/.test(parentClass);
      if (canReuseParent && parent.tagName !== 'BODY' && parent.tagName !== 'HTML') {
        parent.classList.add('ll-canvas-retrofit-stage');
        return parent;
      }
      var wrap = document.createElement('div');
      wrap.className = 'll-canvas-retrofit-stage';
      wrap.style.display = getComputedStyle(canvas).display === 'block' ? 'block' : 'inline-block';
      parent.insertBefore(wrap, canvas);
      wrap.appendChild(canvas);
      return wrap;
    }

    function enhanceCanvas(canvas) {
      if (window.__llElementPickerBridgeInstalled) return;
      if (!canvas || canvas.dataset.llCanvasEnhanced === 'true') return;
      var stage = stageFor(canvas);
      if (!stage || stage.querySelector(':scope > .ll-canvas-retrofit-fullscreen')) return;
      canvas.dataset.llCanvasEnhanced = 'true';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'll-canvas-retrofit-fullscreen';
      btn.setAttribute('data-ll-bridge-ui', 'canvas-fullscreen');
      btn.title = 'Full screen';
      btn.setAttribute('aria-label', 'Open canvas full screen');
      btn.textContent = '[]';
      btn.addEventListener('click', function(event) {
        event.preventDefault();
        event.stopPropagation();
        toggleFullscreen(stage);
      });
      stage.appendChild(btn);
    }

    function enhanceAll(root) {
      if (window.__llElementPickerBridgeInstalled) return;
      ensureStyle();
      Array.prototype.forEach.call((root || document).querySelectorAll('canvas'), enhanceCanvas);
    }

    document.addEventListener('fullscreenchange', emitResize);
    document.addEventListener('keydown', function(event) {
      if (event.key !== 'Escape') return;
      var active = document.querySelector('.ll-canvas-retrofit-fullscreen-fallback');
      if (active) fallbackStage(active, false);
    });

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function() { enhanceAll(document); });
    else enhanceAll(document);
    setTimeout(function() { enhanceAll(document); }, 300);
    setTimeout(function() { enhanceAll(document); }, 1200);
    new MutationObserver(function(records) {
      records.forEach(function(record) {
        Array.prototype.forEach.call(record.addedNodes || [], function(node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'CANVAS') enhanceCanvas(node);
          else if (node.querySelectorAll) enhanceAll(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  })();
</script>`;

function insertBeforeClosingTag(source, snippet, tagName) {
  const pattern = new RegExp(`</${tagName}>`, 'i');
  if (pattern.test(source)) return source.replace(pattern, `${snippet}\n</${tagName}>`);
  return `${source}\n${snippet}`;
}

export function injectCanvasEnhancements(html) {
  const source = String(html || '');
  if (source.includes(CANVAS_ENHANCEMENTS_MARKER)) return source;
  return insertBeforeClosingTag(source, CANVAS_ENHANCEMENTS_SCRIPT, 'body');
}

// Injects a small helper script right before </head> so generated HTML can
// call `await window.askAI("...")` and get back a plain string answer (or
// a rejected promise on error/timeout/rate-limit). Falls back to leaving
// the HTML untouched if there's no </head> to anchor on (shouldn't happen
// for anything that passed the platform's own validation, but this must
// never be the thing that breaks a page).
export function injectAskAIBridge(html, { enhanceCanvas = true } = {}) {
  let source = String(html || '');
  if (enhanceCanvas) source = injectCanvasEnhancements(source);
  if (!/<\/head>/i.test(source)) return source;

  const script = `<script>
    // Shared transport for both bridge calls below. \`payload\` is either
    // { question } (free text, resolves to a string) or { kind, params }
    // (structured, resolves to a validated object) — see
    // functions/contentSchemas.js.
    window.__llAskAISend = function(payload, pick) {
      return new Promise(function(resolve, reject) {
        var requestId = 'ai_' + Date.now() + '_' + Math.random().toString(36).slice(2);
        function onMessage(event) {
          var d = event.data;
          if (!d || d.__llAskAIResult !== true || d.requestId !== requestId) return;
          window.removeEventListener('message', onMessage);
          clearTimeout(timer);
          if (d.error) reject(new Error(d.error));
          else resolve(pick(d));
        }
        window.addEventListener('message', onMessage);
        // This should resolve/reject the instant the actual response
        // arrives, no matter how long that takes — not bail out on some
        // fixed "typical" duration. This timer is a last-resort safety net
        // only (set well above the Cloud Function's own 540s budget and the
        // httpsCallable SDK timeout wrapping it, see askAIInPageFn above),
        // so it should essentially never fire in normal operation — its
        // only job is to stop the page's UI (button/spinner) from being
        // stuck forever in the rare case the relay itself breaks (e.g. tab
        // backgrounded, iframe torn down mid-request).
        var timer = setTimeout(function() {
          window.removeEventListener('message', onMessage);
          reject(new Error('AI request timed out.'));
        }, 570000);
        var message = { __llAskAI: true, requestId: requestId };
        for (var k in payload) { if (Object.prototype.hasOwnProperty.call(payload, k)) message[k] = payload[k]; }
        parent.postMessage(message, '*');
      });
    };

    window.askAI = function(question) {
      return window.__llAskAISend({ question: String(question || '') }, function(d) { return d.answer; });
    };

    // Structured content. Resolves to an OBJECT that the server has already
    // parsed, validated and moderated — never a string to be parsed here.
    // \`kind\` must be one of the server's known kinds; anything else is
    // rejected before it reaches the model.
    window.askAIContent = function(kind, params) {
      return window.__llAskAISend(
        { kind: String(kind || ''), params: params || {} },
        function(d) { return d.content; }
      );
    };

    // Curated content bank — see functions/contentBank.js. Resolves to an
    // ARRAY of whatever opaque item shape this topic's own generation
    // populated its bank with (never an LLM call, never charged). Rejects
    // if this page's context wasn't given an owner to read from (e.g. a
    // purchased/marketplace preview that hasn't wired that up yet) or if
    // the bank is simply empty.
    window.fetchContentBank = function(count) {
      return window.__llAskAISend(
        { bank: true, count: Number(count) || 10 },
        function(d) { return d.items; }
      );
    };
  </script></head>`;

  return source.replace(/<\/head>/i, script);
}

// Wires up the parent-side half for one live iframe. Call this every time
// a topic's HTML is (re)rendered into an iframe the real user is looking
// at — course.js's topic detail preview, studio.js's live preview, AND
// purchased/filed content viewers (course.js's renderPurchaseDetail,
// marketplace.js's openPurchaseViewer) — a buyer can trigger dynamic
// content on a purchased page too, wallet-charged to THEIR OWN account,
// never the original creator's (see functions/askAI.js chargeForAskAI).
// Deliberately NOT used for src/lib/sandbox.js's hidden validation iframe
// (that's an automated check, not a real user, and shouldn't burn AI
// quota/wallet balance).
//
// `bankOnly: true` (used by home.js's openSchoolSubscriptionViewer) keeps
// window.fetchContentBank working — free, no LLM call, no wallet charge —
// while rejecting window.askAI/askAIContent outright, preserving that
// view's deliberate "no chat/generation controls" design (school-subscribed
// students amend nothing; only the school owner edits, from their own
// Studio). Without this flag every request type is served normally.
//
// Returns a detach() function — call it before attaching a new bridge to
// a replacement iframe (course.js/studio.js/marketplace.js all swap
// srcdoc/iframe elements or close modals often), so stale listeners don't
// pile up over a long session.
export function attachAskAIBridge(iframeEl, {
  courseTitle, topicTitle, courseId, topicId, ownerUid, bankOnly = false,
}) {
  // A generated page shouldn't be firing dozens of these; cap concurrent
  // in-flight requests defensively so a buggy loop in generated code can't
  // spam the Cloud Function faster than a human ever would, independent
  // of the server's own per-hour rate limit.
  const MAX_CONCURRENT = 3;
  let inFlight = 0;

  async function onMessage(event) {
    const d = event.data;
    if (!d || d.__llAskAI !== true || event.source !== iframeEl.contentWindow) return;
    const {
      requestId, question, kind, params, bank, count,
    } = d;
    // Three request shapes share this one relay, its concurrency cap and its
    // error logging — only the callable payload and the reply field differ:
    //  - window.fetchContentBank(count) → bank === true (see contentBank.js)
    //  - window.askAIContent(kind, params) → a KNOWN kind, validated
    //    server-side against contentSchemas.js's closed vocabulary
    //  - window.askAI(question) → plain free text
    const isBank = bank === true;
    const isContent = !isBank && typeof kind === 'string' && kind.length > 0;

    const respond = (payload) => {
      iframeEl.contentWindow?.postMessage({ __llAskAIResult: true, requestId, ...payload }, '*');
    };

    if (inFlight >= MAX_CONCURRENT) {
      respond({ error: 'Too many pending requests from this page — wait for one to finish first.' });
      return;
    }

    inFlight += 1;
    try {
      if (bankOnly && !isBank) {
        throw new Error('AI chat is not available in this view.');
      } else if (isBank) {
        if (!ownerUid) throw new Error('This page cannot read a content bank in this context yet.');
        const items = await fetchContentBankItems({
          ownerUid, courseId, topicId, count,
        });
        respond({ items });
      } else if (isContent) {
        const content = await askAIContentInPage({ kind, params, courseTitle, topicTitle });
        respond({ content });
      } else {
        const answer = await askAIInPage({ question, courseTitle, topicTitle });
        respond({ answer });
      }
    } catch (err) {
      const fallbackMessage = isBank ? 'Could not load practice content right now.' : (isContent ? 'Could not build that content right now.' : 'Could not get an answer right now.');
      const displayMessage = friendlyError(err, fallbackMessage);
      respond({ error: displayMessage });
      // Centralized, mass-explorable log (see lib/errorLog.js) — this is
      // the in-page bridge, reachable from every learner-facing generated
      // page, so failures here (rate limits, moderation blocks, the
      // underlying LLM call failing, a client-side abort) are exactly the
      // kind of thing worth seeing in aggregate rather than only as a
      // single inline message on whichever page happened to hit it.
      logClientError({
        context: isBank ? 'contentBank' : 'askAI',
        error: err,
        displayMessage,
        courseId, topicId,
        extra: { questionLength: String(question || '').length, kind: isContent ? kind : undefined },
      });
    } finally {
      inFlight -= 1;
    }
  }

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
